// The Eng Tour's introduction and closing screens — the words on them and the
// completion form's fields — as data the Tour Creator edits ("✎ Intro &
// closing") instead of text written into EngTourPage.
//
// One module for all three readers, so they cannot disagree about a default or
// a field: the viewer renders from it, the creator edits through it, and
// api/send-tour-email.js loads it (dynamic `import()` of a literal path, as
// api/_shuttle.js does) to decide which answers are required and what each one
// is called in the email. Pure and import-free for that reason.
//
// Where it lives in the tour document:
//   welcome.heading / .blurb / .banner   the first screen (fields that existed)
//   welcome.roomGuide { heading, body }  "How to read room numbers"
//   closing { heading, body, thanks, fields, recipients }
//   closing.recipients { to, cc, bcc }   who the completion email goes to —
//                                        editor-only; the public payload drops it
// A document saved before this existed has none of the new keys and reads as
// the defaults below, which are the words the screens used to hard-code —
// except "the EITC Complex", which the editors asked to lose ("C" is Complex).

export const DEFAULT_HEADING = "Welcome to Engineering!";
export const DEFAULT_BLURB = "Pick what you're curious about and we'll show you around.";

export const DEFAULT_ROOM_GUIDE = {
  heading: "How to read room numbers",
  body: [
    "The **EITC** is a group of connected buildings where the prefix tells you which building: **E1**, **E2**, **E3**, or **Stanley Pauley**.",
    "The **first digit** of the room number is the floor. So **270** is on Level 2, **433** is on Level 4, and so on.",
    "The **200 Level is the Main floor** — it's where all the building entrances are, so you're already on Level 2 when you walk in.",
    "For example:",
    "- **E2-229** is in the E2 Building, on Floor 2",
    "- **SP-347** is in Stanley Pauley, on the third floor",
  ].join("\n"),
};

export const DEFAULT_CLOSING = {
  heading: "You did it! 🎉",
  body: "You collected all {stamps} stamps. Leave your info and we'll send you more about studying Engineering at the University of Manitoba.",
  thanks: "You're all set. Free roam is unlocked — explore every student group and space on your own.",
};

// ── Completion form ──────────────────────────────────────────────────────────
// Every field is "required", "optional" or "off". The five built-in ones are
// the boxes the form always had (same ids the email payload always used);
// an editor can relabel them, switch them off, or add their own text
// questions (`custom: true`, id "q_…"). The SERVER takes labels from the saved
// tour, never from the visitor, so a submission cannot put words of its own
// into the operator's inbox beyond the answers themselves.
export const FIELD_MODES = ["required", "optional", "off"];
export const MAX_CUSTOM_FIELDS = 8;
export const MAX_ANSWER = 300;

export const BUILTIN_FIELDS = [
  { id: "name",   label: "Your name",    placeholder: "",                         type: "text",  mode: "required", max: 120 },
  { id: "email",  label: "Email",        placeholder: "",                         type: "email", mode: "required", max: 200 },
  { id: "grade",  label: "Grade / year", placeholder: "e.g. Grade 12",            type: "text",  mode: "optional", max: 60 },
  { id: "city",   label: "City / town",  placeholder: "e.g. Winnipeg",            type: "text",  mode: "optional", max: 80 },
  { id: "school", label: "High school",  placeholder: "e.g. Kelvin High School",  type: "text",  mode: "optional", max: 120 },
];
const BUILTIN_BY_ID = new Map(BUILTIN_FIELDS.map((f) => [f.id, f]));

export const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const str = (v, n) => (typeof v === "string" ? v : "").slice(0, n);
const modeOf = (m, fallback) => (FIELD_MODES.includes(m) ? m : fallback);

/**
 * The form's fields in the order the editor arranged them, every built-in one
 * present exactly once (a saved list that omits one gets it back with its
 * default mode, appended), custom ones kept up to the cap. `mode` is always
 * one of FIELD_MODES. `label` is what visitors and the email read (a built-in
 * field left blank keeps its own name); `rawLabel` is exactly what the editor
 * typed, so clearing a box to retype it doesn't snap back or vanish. A custom
 * question with no label is not asked (`isAsked`).
 */
export function formFields(closing) {
  const saved = Array.isArray(closing?.fields) ? closing.fields : [];
  const out = [];
  const seen = new Set();
  let customs = 0;
  for (const f of saved) {
    if (!f || typeof f.id !== "string" || seen.has(f.id)) continue;
    const base = BUILTIN_BY_ID.get(f.id);
    if (base) {
      seen.add(f.id);
      const raw = typeof f.label === "string" ? str(f.label, 80) : base.label;
      out.push({
        ...base,
        label: raw.trim() || base.label,
        rawLabel: raw,
        placeholder: typeof f.placeholder === "string" ? str(f.placeholder, 80) : base.placeholder,
        mode: modeOf(f.mode, base.mode),
      });
    } else if (f.custom && /^q_[a-z0-9_]{1,40}$/i.test(f.id) && customs < MAX_CUSTOM_FIELDS) {
      const raw = str(f.label, 80);
      seen.add(f.id);
      customs++;
      out.push({ id: f.id, label: raw.trim(), rawLabel: raw, placeholder: str(f.placeholder, 80), type: "text", mode: modeOf(f.mode, "optional"), max: MAX_ANSWER, custom: true });
    }
  }
  for (const base of BUILTIN_FIELDS) if (!seen.has(base.id)) out.push({ ...base, rawLabel: base.label });
  return out;
}

/** Shown to visitors: switched on, and named (an unnamed row would be an unnamed row in the email). */
export const isAsked = (f) => f.mode !== "off" && !!f.label;

/** Everything the intro and closing screens show, defaults filled in. */
export function introContent(data) {
  const w = data?.welcome || {};
  const g = w.roomGuide || {};
  const c = data?.closing || {};
  const pick = (v, d) => (typeof v === "string" && v.trim() ? v : d);
  return {
    banner: typeof w.banner === "string" ? w.banner : "",
    heading: pick(w.heading, DEFAULT_HEADING),
    blurb: pick(w.blurb, DEFAULT_BLURB),
    roomGuide: { heading: pick(g.heading, DEFAULT_ROOM_GUIDE.heading), body: pick(g.body, DEFAULT_ROOM_GUIDE.body) },
    closing: {
      heading: pick(c.heading, DEFAULT_CLOSING.heading),
      body: pick(c.body, DEFAULT_CLOSING.body),
      thanks: pick(c.thanks, DEFAULT_CLOSING.thanks),
      fields: formFields(c),
    },
  };
}

/** "{stamps}" in the closing text becomes the tour's stamp count. */
export function fillStamps(text, n) {
  return String(text || "").replace(/\{stamps\}/gi, String(n));
}

// ── The room guide's light markup ────────────────────────────────────────────
// What an editor types in a plain box: one point per line, a line starting
// with "-" sits under the one above, and **double stars** make words bold.
// Rendered as text nodes by the viewer — never as HTML — so nothing typed
// here can become markup.

/** "a **b** c" → [{ text: "a ", bold: false }, { text: "b", bold: true }, …] */
export function parseInline(line) {
  const parts = String(line || "").split("**");
  // An unpaired "**" (an even number of parts) reads as the two stars it is.
  if (parts.length % 2 === 0) parts.splice(-2, 2, parts[parts.length - 2] + "**" + parts[parts.length - 1]);
  return parts.map((text, i) => ({ text, bold: i % 2 === 1 })).filter((p) => p.text);
}

/** The body as bullet points: [{ depth: 0|1, parts }] — blank lines dropped. */
export function parseGuide(body) {
  return String(body || "")
    .split(/\r?\n/)
    .map((raw) => {
      const m = raw.match(/^\s*[-•*]\s+(.*)$/);
      const text = (m ? m[1] : raw).trim();
      return text ? { depth: m ? 1 : 0, parts: parseInline(text) } : null;
    })
    .filter(Boolean);
}

// ── Submissions ──────────────────────────────────────────────────────────────

/**
 * Checks a visitor's answers against the form. Returns the answers that count
 * (fields switched off are dropped, everything trimmed and clamped), the ids
 * of required fields left blank, and whether an email that WAS given is
 * malformed. The viewer uses it to enable Submit; the server re-runs it on the
 * saved form, which is the check that matters.
 */
export function checkAnswers(fields, answers) {
  const a = answers && typeof answers === "object" ? answers : {};
  const values = {};
  const missing = [];
  let badEmail = false;
  for (const f of fields) {
    if (!isAsked(f)) continue;
    const raw = a[f.id];
    // eslint-disable-next-line no-control-regex
    const v = (typeof raw === "string" || typeof raw === "number" ? String(raw) : "").replace(/[\u0000-\u001f\u007f]+/g, " ").trim().slice(0, f.max || MAX_ANSWER);
    if (!v) { if (f.mode === "required") missing.push(f.id); continue; }
    if (f.type === "email" && !EMAIL_RE.test(v)) { badEmail = true; continue; }
    values[f.id] = v;
  }
  return { values, missing, badEmail, ok: missing.length === 0 && !badEmail };
}

/** A fresh custom question's id: "q_" + something unique enough for one form. */
export function newFieldId() {
  return "q_" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}

// ── Survey recipients ────────────────────────────────────────────────────────
// Who the completion email goes to, set beside the form in "Intro & closing"
// (2026-10-16). Before this it was TOUR_EMAIL_TO alone, and that inbox is
// still where it goes while the lists are empty, and the "To" when only CC /
// BCC are filled (an email needs a To). These are addresses of the people
// running the tour, never shown to visitors: sanitizeForClient drops them.
export const RECIPIENT_KINDS = [
  { kind: "to", label: "Regular recipients", add: "Add regular recipient", hint: "Everyone sees these addresses." },
  { kind: "cc", label: "CC recipients", add: "Add CC recipient", hint: "Copied in; everyone sees these addresses too." },
  { kind: "bcc", label: "BCC recipients", add: "Add BCC recipient", hint: "Get a copy; no one sees these addresses." },
];
export const MAX_RECIPIENTS = 10; // across all three lists

export const cleanAddress = (v) => (typeof v === "string" ? v : "").trim().toLowerCase().slice(0, 254);
export const isEmailAddress = (v) => EMAIL_RE.test(v) && v.length <= 254 && !/[<>",;()\\]/.test(v);

/**
 * The saved lists made safe to send to: valid addresses only, lower-cased,
 * each address once (a To beats a CC beats a BCC), at most MAX_RECIPIENTS in
 * all. The server runs this on whatever it reads, so a hand-edited document
 * cannot widen the mailing.
 */
export function normalizeRecipients(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  const seen = new Set();
  const out = { to: [], cc: [], bcc: [] };
  for (const { kind } of RECIPIENT_KINDS) {
    for (const v of Array.isArray(r[kind]) ? r[kind] : []) {
      const a = cleanAddress(v);
      if (!isEmailAddress(a) || seen.has(a) || seen.size >= MAX_RECIPIENTS) continue;
      seen.add(a);
      out[kind].push(a);
    }
  }
  return out;
}

export const recipientCount = (r) => RECIPIENT_KINDS.reduce((n, { kind }) => n + ((r && r[kind]) || []).length, 0);
